import { ENDPOINTS } from '@/util'
import { OperationSchema, RequestFnInfo, RequestFnOptions, requestFn } from '@openapi-qraft/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createAPIClient } from '@/api'
import { components } from '@/api/schema'
import { isTokenExpired, isTokenNearExpiry } from '@/hooks/useTokenValidator'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { clearAiChatBrowserStorage } from '@/utils/aiChatStorage'
import { createContext, useContext, useMemo } from 'react'

const logoutDueToTokenIssue = () => {
  clearAiChatBrowserStorage()
  sessionStorage.clear()
  window.location.replace('/?authStatus=logout-success')
}

const shouldRetryAuth = async (response?: Response) => {
  if (!response || response.status !== 401) {
    return false
  }

  const authHeader = response.headers.get('www-authenticate')?.toLowerCase() ?? ''
  if (authHeader.includes('invalid_token')) {
    return true
  }

  if (!response.bodyUsed) {
    try {
      const responseText = await response.clone().text()
      if (!responseText.trim()) {
        return true
      }
    } catch {
      // ignore body read failures and fall through
    }
  }

  return false
}

const shouldForceLogout = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' && error.toLowerCase().includes('token expired')
  }

  const response = (error as { response?: Response }).response
  if (response?.status === 401) {
    return true
  }

  const extractMessage = (err: unknown): string | undefined => {
    if (!err) return undefined
    if (typeof err === 'string') return err
    if (typeof err === 'object') {
      const { message, error } = err as { message?: unknown; error?: unknown }
      if (typeof message === 'string') return message
      if (typeof error === 'string') return error
      return extractMessage(error)
    }
    return undefined
  }

  const message =
    extractMessage(error) ||
    extractMessage((error as { error?: unknown }).error) ||
    extractMessage((error as { cause?: unknown }).cause)

  return typeof message === 'string' && message.toLowerCase().includes('token expired')
}

export const createRestClient = (token?: string) => {
  const authenticatedToken = token && !isTokenExpired(token) ? token : undefined
  return createAPIClient({
    baseUrl: ENDPOINTS.DOTNET,
    requestFn: authenticatedToken ? getReqFn(authenticatedToken) : requestFn,
    queryClient,
  }).rest
}

export type RestSchemas = components['schemas']

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // refetchOnWindowFocus: false,
      refetchOnWindowFocus: 'always',
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
})

export const getReqFn = (token: string) => {
  const reqFnWithToken: typeof requestFn = async <TData, TError>(
    schema: OperationSchema,
    requestInfo: RequestFnInfo,
    options?: RequestFnOptions
  ) => {
    if (!token) {
      console.warn('No token found, please login again')
      window.location.replace('/')
      return Promise.reject(new Error('No token found'))
    }
    if (isTokenExpired(token)) {
      console.warn('Token expired, please login again')
      clearAiChatBrowserStorage()
      sessionStorage.clear()
      window.location.replace('/?authStatus=logout-success')
      return Promise.reject(new Error('Token expired'))
    }
    if (isTokenNearExpiry(token)) {
      const refreshToken = async () => {
        try {
          const response = await fetch('/rest/auth/refreshToken', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          if (response.ok) {
            const data = await response.json()
            sessionStorage.setItem('token', JSON.stringify(data.token))
            console.info('Token refreshed successfully.')
          } else {
            console.error('Failed to refresh token')
          }
        } catch {
          console.error('Failed to refresh token')
        }
      }
      await refreshToken()
    }
    const requestInfoWithHeaders = token
      ? {
          ...requestInfo,
          headers: {
            ...(requestInfo.headers ?? {}),
            Authorization: `Bearer ${token}`,
          },
        }
      : requestInfo
    const executeRequest = () => requestFn<TData, TError>(schema, requestInfoWithHeaders, options)

    try {
      let result = await executeRequest()
      if (await shouldRetryAuth(result.response)) {
        await new Promise((resolve) => setTimeout(resolve, 200))
        result = await executeRequest()
      }
      const resultError = (result as { error?: unknown }).error
      if (result.response?.status === 401 || shouldForceLogout(resultError)) {
        console.warn('Token invalidated by server response, logging out.')
        logoutDueToTokenIssue()
      }
      return result
    } catch (error) {
      if (shouldForceLogout(error)) {
        console.warn('Token invalidated by server response, logging out.')
        logoutDueToTokenIssue()
      }
      throw error
    }
  }
  return reqFnWithToken
}

const RestClientContext = createContext<ReturnType<typeof createRestClient>>({} as ReturnType<typeof createRestClient>)

const RestClientProvider = ({ children }: { children: React.ReactNode }) => {
  const [token] = useSessionStorage('token', undefined)
  const restClient = useMemo(() => {
    return createRestClient(token)
  }, [token])
  return <RestClientContext.Provider value={restClient}>{children}</RestClientContext.Provider>
}

export const useRestClient = () => {
  const restClient = useContext(RestClientContext)
  if (!restClient) {
    throw new Error('RestClient not found')
  }
  return restClient
}

export const QueryProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <RestClientProvider>
        {children}
        {/* <ReactQueryDevtools position="bottom" buttonPosition="bottom-right" /> */}
      </RestClientProvider>
    </QueryClientProvider>
  )
}
